import type { BundleQuizQuestion } from '@arianna/shared'

// ---------------------------------------------------------------------------
// QuizSessionScore
//
// Mirrors apps/reader/src/db/types.ts QuizSessionScore.
// Kept here so both web and iPad can type their session scores without
// depending on the native DB layer.
// ---------------------------------------------------------------------------

export interface QuizSessionScore {
  correct: number
  wrong: number
  skipped: number
  total: number
}

// ---------------------------------------------------------------------------
// computeQuizSessionScore
//
// Canonical session-level scoring function (iPad is the rule).
// Returns { correct, wrong, skipped, total }.
//
// Detailed wrong-question lists are computed locally by each UI component's
// Results phase — they are display concerns, not scoring concerns.
// ---------------------------------------------------------------------------

export function computeQuizSessionScore(
  questionIds: string[],
  answers: Record<string, number | null>,
  allQuestions: BundleQuizQuestion[],
): QuizSessionScore {
  let correct = 0
  let wrong = 0
  let skipped = 0
  for (const qid of questionIds) {
    const ans = answers[qid]
    const q = allQuestions.find((qq) => qq.id === qid)
    if (ans === null || ans === undefined) {
      skipped++
    } else if (q != null && ans === q.correctOptionIdx) {
      correct++
    } else {
      wrong++
    }
  }
  return { correct, wrong, skipped, total: questionIds.length }
}

// ---------------------------------------------------------------------------
// shuffleArray
//
// Fisher-Yates in-place shuffle (returns a new array).
// Both web and iPad used identical implementations under different names.
// ---------------------------------------------------------------------------

export function shuffleArray<T>(arr: T[]): T[] {
  const result = [...arr]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j]!, result[i]!]
  }
  return result
}

// ---------------------------------------------------------------------------
// fmtDate / fmtTime
//
// Pure timestamp formatters (no DOM, no i18n framework).
// Used in session cards in hub and results phases.
// ---------------------------------------------------------------------------

const MONTHS_IT = [
  'gen',
  'feb',
  'mar',
  'apr',
  'mag',
  'giu',
  'lug',
  'ago',
  'set',
  'ott',
  'nov',
  'dic',
] as const

export function fmtDate(ts: number): string {
  const d = new Date(ts)
  const diff = Math.floor((Date.now() - ts) / 86400000)
  if (diff === 0) return 'oggi'
  if (diff === 1) return 'ieri'
  if (diff < 7) return `${diff}g fa`
  return `${d.getDate()} ${MONTHS_IT[d.getMonth()] ?? ''}`
}

export function fmtTime(ts: number): string {
  const d = new Date(ts)
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// buildQuizHubNarrative
//
// Produces the serif narrative string shown at the top of the Hub phase.
// Both web and iPad had identical logic with different property access
// patterns (camelCase vs snake_case) on their session types.
//
// Takes a normalized list so callers can map their platform-specific session
// types without coupling this module to DB or API shapes.
// ---------------------------------------------------------------------------

export interface QuizHubSessionSummary {
  completedAt: number | null
  score: { correct: number; total: number } | null
}

export function buildQuizHubNarrative(sessions: QuizHubSessionSummary[]): string {
  const inProgress = sessions.find((s) => s.completedAt == null) ?? null
  const completed = sessions
    .filter((s) => s.completedAt != null)
    .sort((a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0))

  if (sessions.length === 0) {
    return 'Non hai ancora fatto verifiche su questo manuale. Quando sei pronto, avviane una — è il modo più veloce per capire dove sei solido.'
  }

  if (inProgress != null && completed.length === 0) {
    return "Hai una verifica in sospeso. Puoi riprenderla da dove l'hai lasciata o scartarla e iniziarne una nuova."
  }

  if (inProgress == null && completed.length === 1) {
    const s = completed[0]!
    const sc = s.score
    const pct = sc != null && sc.total > 0 ? Math.round((sc.correct / sc.total) * 100) : 0
    return pct >= 75
      ? `Prima verifica: ${sc?.correct ?? 0}/${sc?.total ?? 0}. Buon inizio — sei già solido sulla base.`
      : `Prima verifica: ${sc?.correct ?? 0}/${sc?.total ?? 0}. C'è margine. Ripassala e rifai la verifica.`
  }

  if (inProgress == null && completed.length >= 2) {
    const last = completed[0]!
    const prev = completed[1]!
    const lastPct =
      last.score != null && last.score.total > 0
        ? Math.round((last.score.correct / last.score.total) * 100)
        : 0
    const prevPct =
      prev.score != null && prev.score.total > 0
        ? Math.round((prev.score.correct / prev.score.total) * 100)
        : 0
    if (lastPct > prevPct) {
      return `Hai fatto ${completed.length} verifiche. L'ultima: ${last.score?.correct ?? 0}/${last.score?.total ?? 0} — stai migliorando.`
    } else if (lastPct < prevPct) {
      return `Hai fatto ${completed.length} verifiche. L'ultima: ${last.score?.correct ?? 0}/${last.score?.total ?? 0} — vale la pena ripassare.`
    } else {
      return `Hai fatto ${completed.length} verifiche. L'ultima: ${last.score?.correct ?? 0}/${last.score?.total ?? 0}.`
    }
  }

  // inProgress != null && completed.length >= 1. Spelled-out "Una" for the
  // singular: the sentence opens the page as its lead — starting it with a
  // bare digit reads wrong, and "1 completata, una in sospeso" mixed digit
  // and word in the same breath.
  const n = completed.length
  const done = n === 1 ? 'Una verifica completata' : `${n} verifiche completate`
  return `${done}${inProgress != null ? ', una in sospeso. Riprendi o inizia da capo.' : '.'}`
}

// ---------------------------------------------------------------------------
// classifyQuizResultsNarrative
//
// Score-percentage → narrative tier for the results-screen narrative
// sentence, shared by web (`packages/reader-ui/src/quiz/QuizResults.tsx`)
// and mobile (`apps/reader/src/screens/quiz/QuizResultsScreen.tsx`) so the
// breakpoints can't drift between the two the way they had (75/50 on mobile
// vs 80/60 on web — TASK-019 Phase 5 review). Text stays platform-local
// (hardcoded Italian on web, i18n keys on mobile) — only the thresholds and
// their ordering are shared, since that's the actual invariant.
// ---------------------------------------------------------------------------

export type QuizResultsNarrativeTier = 'strong' | 'mid' | 'weak'

export function classifyQuizResultsNarrative(pct: number): QuizResultsNarrativeTier {
  if (pct >= 80) return 'strong'
  if (pct >= 60) return 'mid'
  return 'weak'
}
